import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ActiveSuspension, CarSharingInvoicesResponse, CarSharingPaymentsBody, CarSharingPaymentsResponse, CarSharingReservation, CarSharingSubscription, CheckFCRequest, CustomerSearchDataPagination, CustomerSearchFilters, GetReservationHistoryParams, HistorySuspension, IGetUniqueIDParams, InvoiceServiceRequestFilter, LinkCustomerData, OmooveCustomerResponse, OmooveModDataResponse, PaymentRequestFilter, ReservationHistoryRequest } from "../../types";
import i18n from "../../../../config/dictionary/i18n";

// Helper per ottenere il client carsharing dalla configurazione
export const getCarsharingClient = (): string => {
  // @ts-ignore
  return window?.appConfig?.CARSHARING_CLIENT || 'fcapre';
};


export const searchCustomerByFilters = (queryFilters: any) => {
  return axios.post("/customer/search", queryFilters);
};

export const getCustomerBaseById = (id: any) => {
  return axios.get(`/customer/${id}`);
}

export const searchCustomerByFiltersAsync = async (filters: CustomerSearchDataPagination) => {
  const resp = await axios.post("/customer/search", filters);
  return resp.data;
};

export const getCustomerBaseByIdAsync = async (id: any) => {
  const resp = await axios.get(`/customer/${id}`);
  return resp.data;
}

export const getCustomerBaseStatusHistoryAsync = async (id: any) => {
    //const resp = await axios.get(`/customer/${id}`);
    const resp = await axios.get(`/customer/status-history/${id}`);

    return resp.data;
}

// CR-DRIVALIA-EPCH (LPP-22837): change log cliente (cambi e-mail e telefono)
export const getCustomerBaseChangeLogAsync = async (id: any) => {
    const resp = await axios.get(`/customer/change-log/${id}`);

    return resp.data;
}

export const getCustomerBaseByUniqueId = (queryParams: IGetUniqueIDParams) => {
  return axios.get("/customer", { params: queryParams });
}

export const findCustomerBaseByUniqueData = (queryBody: CustomerSearchFilters) => {
  return axios.post("/customer/find-by-unique-data", queryBody)
}

export const findCustomerBaseByUniqueDataAsync = async (queryBody: CustomerSearchFilters) => {
  const resp = await axios.post("/customer/find-by-unique-data", queryBody);
  return resp.data;
}

/*export const getCustomerBaseByUniqueIdAsync = async (taxCode: string | null, drvLicenceNumber: string | null, type: string | null) => {
    const resp = await axios.get("/customer", {params: {taxCode, drvLicenceNumber, type}});
    return resp.data;
}*/

export const editCustomerbaseFields = ({ queryKey }: any) => {
  const [_key, { queryFields }] = queryKey;
  return axios.put("/customer", queryFields);
}

/**
 * CR-DRIVALIA-EPCH (LPP-22837): valore che CB interpreta come "sgancia l'identita' digitale"
 * (CustomerModifierService.applyIdpUidModifies). Coincide per caso con CBOmissionType.OMISSION_DASH,
 * il segnaposto di visualizzazione: qui ha un significato diverso e non va confuso con quello.
 */
export const CB_IDP_UNLINK_MARKER = "--";

/**
 * CR-DRIVALIA-EPCH (LPP-22837): sgancia l'identita' digitale (idpUid) del customer.
 * Payload minimo: CB applica solo i campi presenti, il resto del customer non viene toccato.
 * Chiamata autonoma, fuori dal salvataggio multi-sezione: l'effetto coincide con la conferma.
 */
export const unlinkDigitalIdentity = async (cbCustomerId: number | string) => {
  const resp = await axios.put("/customer", { cbCustomerId: Number(cbCustomerId), idpUid: CB_IDP_UNLINK_MARKER });
  return resp.data;
}

export const createCustomerBase = (({ queryKey }: any) => {
  const [_key, { queryFields }] = queryKey;
  return axios.post("/customer", queryFields);
})

export const getSapEnumConfiguration = () => {
  return axios.get("/customer/sap-configuration");
}

export const getSapConfigurationByCountry = (country: string) => {
  //console.log('Country:', country)
  return axios.get(`/customer/sap-configuration-by-country/${country}`);
}

export const useGetCustomerByIdMetaQueryObserver = (id?: string) => {
  return useQuery({
    queryKey: ['getCustomerBaseById', id],
    enabled: false
  });
}

export const getMobilePrefix = () => {
  return axios.get("/customer/prefix");
}

export const verifyTaxCode = ((request: CheckFCRequest) => {
  return axios.post("/customer/verify/taxcode", request);
})

export const verifyTaxCodeAsync = async (request: CheckFCRequest) => {
  const resp = await axios.post("/customer/verify/taxcode", request);
  return resp;
}

export const getCountries = () => {
  return axios.get("/customer/countries");
}

interface IGetCountriesReturn {
  data?: {
    message?: string,
    code?: string,
    data?: string[]
  }
}

export const useGetCountriesMetaQueryObserver = () => {
  return useQuery<IGetCountriesReturn | null>({
    queryKey: ['getCountries'],
    queryFn: () => {
      return getCountries();
    },
    enabled: false
  });
}

export const getProvincesByCountry = (country: string) => {
  return axios.get(`/customer/provinces/${country}`);
}

export const getProvinces = () => {
  return axios.get("/customer/provinces");
}

export const getCargosPlaces = (province?: string, issueDate?: string) => {
  return axios.get("customer/cargos/places", { params: { province: province, issueDate: issueDate } });
}

export const linkCustomer = (body: LinkCustomerData) => {
  return axios.post("/customer/link-customer", body);
}

export const unlinkCustomer = (body: LinkCustomerData) => {
  return axios.post("/customer/unlink-customer", body);
}

export const getDossierOriginOption = async (): Promise<string[]> => {
  const response = await axios.get("/customer/dossier-origin");
  return response.data;
}

export const getStatusReasons = async () => {
    const res = await axios.get("/customer/status-reasons");
    return res.data;
}

export const updateCustomerStatus = async (req: any) => {
    const res = await axios.post("/customer/update-status", req);
    return res.data;
}

export const getOmooveModData = async (
  personId: string,
  client = getCarsharingClient(),
  lang = "it"
): Promise<OmooveModDataResponse> => {
  const { data } = await axios.get<OmooveModDataResponse>(
        "/carsharing/customer/mod-data",

           { params: { client, personId, lang } }
        );
    return data;
};


export const getOmooveCustomer = async (
  customerId: string,
  client = getCarsharingClient(),
  market = "it"
): Promise<OmooveCustomerResponse> => {
        const { data } = await axios.get<OmooveCustomerResponse>("/carsharing/customer", {
        params: { client, id: customerId, market },
  });
    return data;
};


export const getActiveSubscriptions = async (
  customerId: string,
  personId: number,
  client = getCarsharingClient()
): Promise<CarSharingSubscription[]> => {
  const { data } = await axios.get<CarSharingSubscription[]>("/carsharing/customer/subscription/active", {
    params: { client, customerId, personId },
  });
  return data;
};

export const getHistorySubscriptions = async (
  customerId: string,
  personId: number,
  client = getCarsharingClient()
): Promise<CarSharingSubscription[]> => {
  const { data } = await axios.get<CarSharingSubscription[]>("/carsharing/customer/subscription/history", {
    params: { client, customerId, personId },
  });
  return data;
};


export const getReservationHistory = async (
  params: GetReservationHistoryParams,
  client = getCarsharingClient(),
  languageId = 'IT'
): Promise<CarSharingReservation[]> => {
  const { personId, contractId, startDate, endDate } = params;

  const end = endDate ? new Date(endDate) : new Date();
  const start = startDate ? new Date(startDate) : new Date(new Date().setFullYear(new Date().getFullYear() - 4));

  const requestBody: ReservationHistoryRequest = {
    retrieveReservationHistoryService: {
      arg0: {
        reservationCustomerData: {
          contractIdList: contractId ? [String(contractId)] : [],
          personID: personId ?? 0,
          personStateID: 1,
          personTypeId: 10001
        },
        startDate: {
          day: start.getDate(),
          month: start.getMonth() + 1,
          year: start.getFullYear(),
          hour: 0, minute: 0, second: 0, timezone: 60,
        },
        endDate: {
          day: end.getDate(),
          month: end.getMonth() + 1,
          year: end.getFullYear(),
          hour: 23, minute: 59, second: 59, timezone: 60,
        },
      },
    },
  };

  const qs = new URLSearchParams({ client, languageId }).toString();

  try {
    const { data } = await axios.post<CarSharingReservation[]>(
      `/carsharing/customer/reservation/history?${qs}`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': languageId.toLowerCase() + '-IT',
        },
      }
    );

    return data ?? [];
  } catch (error: any) {
    if (error.response?.status === 500) return [];
    throw error;
  }
};




export const getActiveSuspensions = async (
  personId: number,
  contractId: number,
  client = getCarsharingClient(),
  languageId = "it"
): Promise<ActiveSuspension[]> => {
  const { data } = await axios.get<ActiveSuspension[]>("/carsharing/customer/suspension/active", {
    params: { client, languageId, personId, contractId },
  });
  return data;
};

export const getHistorySuspensions = async (
  personId: number,
  contractId: number,
  client = getCarsharingClient(),
  languageId = "it"
): Promise<HistorySuspension[]> => {
  const { data } = await axios.get<HistorySuspension[]>("/carsharing/customer/suspension/history", {
    params: { client, languageId, personId, contractId },
  });
  return data;
};



export const getPayments = async (
  filter: InvoiceServiceRequestFilter,
  client = getCarsharingClient(),
  lang?: string
): Promise<CarSharingPaymentsBody> => {
  
  const rawLang = (lang ?? i18n.language ?? 'it').toLowerCase();
  const shortLang = rawLang.split('-')[0]; 

 
  const acceptLanguage = rawLang.includes('-')
    ? rawLang
    : `${shortLang}-${shortLang.toUpperCase()}`; 

  const payload = {
    invoiceServiceRequest: {
      ...filter,
      reservationid: filter.reservationid?.trim() || undefined,
      contractId: filter.contractId?.filter(Boolean),
      personIds: filter.personIds?.filter(Boolean),
      languageId: filter.languageId ?? '10002',
    },
  };

  const { data } = await axios.post<CarSharingPaymentsBody>(
    '/carsharing/customer/payment',
    payload,
    {
      params: { client, lang: shortLang },
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Accept-Language': acceptLanguage,
      },
    }
  );

  return data;
};

 


export const getInvoices = async (
    csCustomerId: string,
    client = getCarsharingClient(),
    page = 0,
    lang: 'it' | 'en' | 'fr' = 'it'
  ): Promise<CarSharingInvoicesResponse> => {

    const languageMap: Record<string, string> = {
      it: 'it-IT',
      en: 'en-US',
      fr: 'fr-FR',
    };
    const acceptLanguage = languageMap[lang] ?? 'it-IT';

    const { data } = await axios.get<CarSharingInvoicesResponse>("/carsharing/customer/invoice", {
      params: { page, client, customerId: Number(csCustomerId) },
      headers: {
        "Accept-Language": acceptLanguage,
      },
    });
    return data;
  };

export const downloadInvoice = async (
    invoiceId: string,
    market: string,
): Promise<Blob> => {
    const client = getCarsharingClient();
    const response = await axios.get<Blob>(
        `/carsharing/customer/invoice/download`,
        {
            params: {
                invoiceId,
                market,
                client,
            },
            responseType: 'blob',
        }
    );
    return response.data;
};



